import * as React from 'react';

import { InputAdornment, Select } from '@mui/material';
import { CountryIso2, FlagImage, defaultCountries, parseCountry, usePhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';

import { Divider, ListSubheader, MenuItem, TextField, TextFieldProps, Typography } from '@emburse/ui-core';

interface IPhoneNumberInputProps {
  value?: string;
  disabled?: boolean;
  inputProps?: object;
  onChange: (event: any) => void;
  preferredCountries?: string[];
  defaultCountry?: string;
  errorMessage?: any;
  helperText?: string;
  disableCountryDropdown?: boolean;
  excludeCountries?: string[];
  isValid?: (phoneNumber: string) => boolean;
}

export const PhoneNumberInput: React.FC<IPhoneNumberInputProps & Partial<TextFieldProps>> = ({
  value,
  disabled,
  inputProps,
  onChange,
  preferredCountries,
  defaultCountry,
  errorMessage,
  helperText,
  disableCountryDropdown,
  excludeCountries,
  isValid,
  ...restProps
}) => {
  const [numberValidationError, setNumberValidationError] = React.useState('');

  const defaultCountriesAsMap: any = React.useRef(
    defaultCountries.reduce((acc, country) => {
      const parsedCountry = parseCountry(country);
      acc[parsedCountry.iso2] = {
        parsedCountry,
        country
      };
      return acc;
    }, {} as any)
  );

  // handle exclusion and disableCountryDropdown logic
  const [allowedCountryList, setAllowedCountryList] = React.useState(defaultCountries);
  React.useEffect(() => {
    let allowedCountries;
    if (disableCountryDropdown) {
      const allowedCountry = defaultCountriesAsMap.current[defaultCountry || 'us']?.country;
      allowedCountries = [allowedCountry];
    } else if (excludeCountries) {
      const excludeSet = new Set(excludeCountries);
      const filteredList = defaultCountries.filter((c) => {
        const parsedCountry = parseCountry(c);
        return !excludeSet.has(parsedCountry.iso2);
      });
      allowedCountries = filteredList;
    }

    if (allowedCountries) {
      setAllowedCountryList(allowedCountries);
    }
  }, [excludeCountries, disableCountryDropdown, defaultCountry]);

  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
    defaultCountry: defaultCountry || 'us',
    value,
    countries: allowedCountryList,
    onChange: (data) => {
      const countryCodeLength = data?.country?.dialCode?.length || 0;
      if (data.phone?.length <= countryCodeLength) {
        return;
      }
      onChange(data.phone);
      if (isValid) {
        const phoneNumber = data.phone.replace(/\D/g, '');
        if (!isValid(phoneNumber)) {
          setNumberValidationError('Invalid phone number');
        } else {
          setNumberValidationError('');
        }
      }
    }
  });

  // the user country is part of the exclusion list or is not allowed
  React.useEffect(() => {
    if (!country) {
      const defaultCountryIso2 = defaultCountry || 'us';
      if (!excludeCountries?.includes(defaultCountryIso2)) {
        setCountry(defaultCountryIso2);
      } else {
        // if the default country is excluded, fallback to US
        setCountry('us');
      }
    }
  }, [country]);

  // handle input parsing
  const textInputOnChange = (event: any) => {
    const newValue = event.target.value || '';
    const countryDialCode = country?.dialCode || '';
    // prevent deleting the country code
    if (newValue.length <= countryDialCode.length + 1) {
      event.target.value = `+${countryDialCode}`;
    } else if (!newValue.startsWith(`+${countryDialCode}`)) {
      // prevent manually entering a different country code
      const onlyDigits = newValue.replace(/\D/g, ''); // ensure we can still process weird input like ++1
      if (!onlyDigits.startsWith(countryDialCode)) {
        if (onlyDigits.slice(1, countryDialCode.length + 1) === countryDialCode) {
          // user just added a digit to the front
          const newNumber = `${onlyDigits[0]}${onlyDigits.slice(countryDialCode.length + 1)}`;
          event.target.value = `+${countryDialCode}${newNumber}`;
        } else {
          // user copy pasted or something, force value instead
          event.target.value = `+${countryDialCode}${onlyDigits}`;
        }
      }
    }

    handlePhoneValueChange(event);
  };

  React.useEffect(() => {
    if ((value?.length || 0) <= 1) {
      setCountry(country?.iso2);
    }
  }, [value]);

  return (
    <TextField
      variant="outlined"
      label="Phone number"
      color="primary"
      placeholder="phone_number"
      value={inputValue}
      onChange={textInputOnChange}
      type="tel"
      inputRef={inputRef}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Select
              data-testid="phone-country-select"
              MenuProps={{
                style: {
                  height: '300px',
                  width: '360px',
                  top: '10px',
                  left: '-34px'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                }
              }}
              sx={{
                width: 'max-content',
                // Remove default outline (display only on focus)
                fieldset: {
                  display: 'none'
                },
                '&.Mui-focused:has(div[aria-expanded="false"])': {
                  fieldset: {
                    display: 'block'
                  }
                },
                // Update default spacing
                '.MuiSelect-select': {
                  padding: '8px',
                  paddingRight: '24px !important'
                },
                svg: {
                  right: 0
                }
              }}
              disabled={disableCountryDropdown}
              value={country?.iso2}
              onChange={(e) => setCountry(e.target.value as CountryIso2)}
              renderValue={(iso2Val) => <FlagImage iso2={iso2Val} style={{ display: 'flex' }} />}
            >
              {(preferredCountries?.length || 0) >= 1 &&
                preferredCountries?.map((iso2) => {
                  if (!iso2) {
                    return null;
                  }
                  const preferredParsedCountry = defaultCountriesAsMap.current[iso2].parsedCountry;
                  if (!preferredParsedCountry) {
                    return null;
                  }
                  return (
                    <MenuItem key={preferredParsedCountry.iso2} value={preferredParsedCountry.iso2}>
                      <FlagImage iso2={preferredParsedCountry.iso2} style={{ marginRight: '8px' }} />
                      <Typography marginRight="8px">{preferredParsedCountry.name}</Typography>
                      <Typography color="gray">+{preferredParsedCountry.dialCode}</Typography>
                    </MenuItem>
                  );
                })}
              {(preferredCountries?.length || 0) >= 1 && (
                <ListSubheader>
                  <Divider />
                </ListSubheader>
              )}

              {allowedCountryList.map((c) => {
                const parsedCountry = parseCountry(c);
                return (
                  <MenuItem key={parsedCountry.iso2} value={parsedCountry.iso2}>
                    <FlagImage iso2={parsedCountry.iso2} style={{ marginRight: '8px' }} />
                    <Typography marginRight="8px">{parsedCountry.name}</Typography>
                    <Typography color="gray">+{parsedCountry.dialCode}</Typography>
                  </MenuItem>
                );
              })}
            </Select>
          </InputAdornment>
        ),
        sx: {
          height: '40px'
        },
        ...inputProps
      }}
      fullWidth
      error={errorMessage || numberValidationError}
      helperText={errorMessage || numberValidationError || helperText}
      {...restProps}
    />
  );
};
